import * as store from "../state/store.js";
import { toast } from "./ui.js";
import { exportDxf as writeDxf } from "../dxf/exporter.js";
import { codeFromUnit } from "./units.js";

// POSTs a cleaned DXF to /convert-to-glb and streams progress via
// Server-Sent Events. The server side (heal_server.py in dev; the Rack host
// in prod) runs dxf_2_gltf.convert() and emits `data: {...}` chunks at
// checkpoints: unit detect → clean → contours → fill → triangulate → GLB
// write. The final chunk carries the GLB payload base64-encoded.
//
// EventSource is a GET-only API and we need to POST the DXF body, so we
// consume the response via fetch's ReadableStream and parse SSE frames
// ourselves — a couple of dozen lines of framing beats moving the DXF into
// a query string or forcing a stateful two-endpoint job model.
//
// A single "current" job is tracked via an AbortController so a new load
// while the last GLB is still building cancels the previous stream instead
// of racing to overwrite glbUrl with stale bytes.

let currentAbort: AbortController | null = null;

export async function startGlbBuild(cleanedDxf: string): Promise<void> {
  // Cancel any in-flight build so a rapid-fire load doesn't leave a stale
  // stream writing over the newer one's progress.
  currentAbort?.abort();
  currentAbort = new AbortController();
  const signal = currentAbort.signal;

  // Drop any prior blob URL so we don't leak object URLs across loads.
  const prior = store.glbUrl();
  if (prior) URL.revokeObjectURL(prior);

  store.batch(() => {
    store.write.glbUrl(null);
    store.write.glbActive(true);
    store.write.glbProgress(0);
  });

  try {
    const res = await fetch("/convert-to-glb", {
      method: "POST",
      headers: { "Content-Type": "application/octet-stream" },
      body: cleanedDxf,
      signal,
    });
    if (!res.ok || !res.body) {
      const text = await res.text().catch(() => "");
      toast(
        `3D build failed (${res.status}${text ? `: ${text.slice(0, 80)}` : ""})`
      );
      return;
    }

    // Stream + incrementally parse SSE. Frames are separated by a blank
    // line ("\n\n"); each frame's lines that start with "data: " concat
    // into a JSON payload we act on.
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let doneReading = false;
    while (!doneReading) {
      const { value, done } = await reader.read();
      doneReading = done;
      if (value) buffer += decoder.decode(value, { stream: !done });

      let idx: number;
      while ((idx = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const payload = frame
          .split("\n")
          .filter((l) => l.startsWith("data:"))
          .map((l) => l.slice(5).replace(/^ /, ""))
          .join("\n");
        if (!payload) continue;
        let msg: unknown;
        try {
          msg = JSON.parse(payload);
        } catch {
          continue;
        }
        if (!msg || typeof msg !== "object") continue;
        const m = msg as {
          error?: string;
          pct?: number;
          step?: string;
          done?: boolean;
          glb?: string;
        };
        if (m.error) {
          toast(`3D build failed: ${m.error.slice(0, 120)}`);
          return;
        }
        if (typeof m.pct === "number") {
          store.write.glbProgress(Math.max(0, Math.min(1, m.pct)));
        }
        if (m.done && typeof m.glb === "string") {
          const bytes = base64ToBytes(m.glb);
          // The Uint8Array's buffer is a plain ArrayBuffer (we allocated it
          // ourselves), but TS's default DOM typing widens ArrayBufferLike
          // to include SharedArrayBuffer. Cast to satisfy BlobPart.
          const blob = new Blob([bytes.buffer as ArrayBuffer], {
            type: "model/gltf-binary",
          });
          const url = URL.createObjectURL(blob);
          store.batch(() => {
            store.write.glbUrl(url);
            store.write.glbProgress(1);
          });
          toast("3D preview ready");
          return;
        }
      }
    }
  } catch (err) {
    if ((err as { name?: string } | null)?.name === "AbortError") return;
    toast(
      `3D build failed (${err instanceof Error ? err.message : "network error"})`
    );
  } finally {
    // Only clear glbActive if this run wasn't superseded by a newer one.
    if (currentAbort?.signal === signal) {
      currentAbort = null;
      store.write.glbActive(false);
    }
  }
}

// Serialise the current model + layer/unit state via writeDxf and kick off
// a fresh /convert-to-glb build. Used by any op that changes what geometry
// belongs to what layer — `dxf_2_gltf.rings_to_faces` groups rings BY
// LAYER and runs even-odd XOR fill inside each layer, so re-partitioning
// entities (delete/merge/rename/move) can change the extruded 3D shape,
// not just its metadata. `applyUnitSettings` also uses this so a unit
// change re-scales the preview.
//
// **Hidden layers are excluded from the 3D preview.** The 3D view mirrors
// what's visible in 2D — flipping a layer's eye icon off makes its
// entities disappear from both. Regular file-download exports still
// include hidden entities (see `ops/io.exportAndDownload`) so the user's
// downloaded DXF is never lossy.
//
// Safe to call rapidly — `startGlbBuild`'s AbortController cancels any
// in-flight build before spawning the new one, so a burst of layer
// changes coalesces to a single successful GLB.
//
// No-op when the model is empty (after filtering) or the store isn't
// loaded — avoids POSTing an empty DXF to /convert-to-glb, which would
// just error out with "No closed cut contours found".
export function rebuildGlbFromCurrent(): void {
  if (!store.isLoaded()) return;
  const allEntities = store.entities();
  const layers = store.layers();
  // Filter to entities whose layer is visible. An entity whose layer went
  // missing (shouldn't happen, but defensive) is treated as hidden.
  const entities = allEntities.filter((e) => layers[e.layer]?.visible ?? false);
  if (entities.length === 0) return;
  const content = writeDxf({
    entities,
    layers,
    unitCode: codeFromUnit(store.dxfUnit()),
    scale: 1,
    customVars: store.customVars(),
  });
  void startGlbBuild(content);
}

// Called by closeDxf to fully drop the cached 3D preview and cancel any
// in-flight build. Also revokes the object URL so we don't leak.
export function resetGlb(): void {
  currentAbort?.abort();
  currentAbort = null;
  const url = store.glbUrl();
  if (url) URL.revokeObjectURL(url);
  store.batch(() => {
    store.write.glbUrl(null);
    store.write.glbActive(false);
    store.write.glbProgress(0);
  });
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
